// EzTreeParams — Set14 P3 §4.3 b: the FLAT (scenery param key) ⇄ NESTED (`TreeOptions`) mapping.
// ez-tree's `TreeOptions` is nested and per-branch-level; our `params` is a flat
// `ParamSchemaWithSpread[]` driving an auto-generated UI. This module owns that reconciliation in
// ONE place, used by BOTH the generator (Gen_EzTree) and the preset-kind codegen (P5), so the two
// can never disagree about what a flat key means.
//
// FLATTENING RULE: per-level options become one param per (option, level) pair — e.g.
// `branchLength0..3`, `branchChildren0..2`, `branchAngle1..3`. Declared at MAX levels; Gen_EzTree
// ignores keys above the resolved `levels` (§4.3 b). Only the FLAT KEYS this module declares are
// ever read — an unknown flat key in a preset override is caught by the P5/P6 test, never silently
// ignored.
//
// This module is DB-FREE (imports only @MaterialTypes for the ParamValue carrier) so the Node test
// lane can round-trip it directly.
import type {ParamValue} from "../../Materials/@MaterialTypes.js";
import {TreeOptions, type TreeOptionsBranch, type LevelMap} from "@venryx/ez-tree";

// ── Flat-key builders ────────────────────────────────────────────────────────────────────────
// The (option, level) → flat-key naming. Centralised so the generator, the kind def, and any test
// share ONE definition of a key's meaning. `levels` is the recursion depth; per-level maps keyed
// by branch level (0 = trunk, then children).

/** Flat key for a per-level branch option: `${option}${level}` (e.g. `branchLength2`). */
export function levelKey(option: string, level: number): string { return `${option}${level}`; }

/** The branch-level map an ez-tree `TreeOptions.branch` expects from a flat params object. */
export interface FlatBranchOptions {
	branchLevels?: number;
	branchAngle0?: number; branchAngle1?: number; branchAngle2?: number; branchAngle3?: number;
	branchChildren0?: number; branchChildren1?: number; branchChildren2?: number;
	branchGnarliness0?: number; branchGnarliness1?: number; branchGnarliness2?: number; branchGnarliness3?: number;
	branchLength0?: number; branchLength1?: number; branchLength2?: number; branchLength3?: number;
	branchRadius0?: number; branchRadius1?: number; branchRadius2?: number; branchRadius3?: number;
	branchSections0?: number; branchSections1?: number; branchSections2?: number; branchSections3?: number;
	branchSegments0?: number; branchSegments1?: number; branchSegments2?: number; branchSegments3?: number;
	branchStart1?: number; branchStart2?: number; branchStart3?: number;
	branchTaper0?: number; branchTaper1?: number; branchTaper2?: number; branchTaper3?: number;
	branchTwist0?: number; branchTwist1?: number; branchTwist2?: number; branchTwist3?: number;
}

/** The complete set of FLAT KEYS the scenery system understands. Used by the P5 preset-codegen to
 *  validate that a preset's overrides all resolve to declared keys, and by Gen_EzTree to avoid
 *  reading non-existent keys. */
export const EZTREE_FLAT_KEYS: readonly string[] = [
	// Shape / branch structure
	"branchLevels",
	"branchAngle0", "branchAngle1", "branchAngle2", "branchAngle3",
	"branchChildren0", "branchChildren1", "branchChildren2",
	"branchGnarliness0", "branchGnarliness1", "branchGnarliness2", "branchGnarliness3",
	"branchLength0", "branchLength1", "branchLength2", "branchLength3",
	"branchRadius0", "branchRadius1", "branchRadius2", "branchRadius3",
	"branchSections0", "branchSections1", "branchSections2", "branchSections3",
	"branchSegments0", "branchSegments1", "branchSegments2", "branchSegments3",
	"branchStart1", "branchStart2", "branchStart3",
	"branchTaper0", "branchTaper1", "branchTaper2", "branchTaper3",
	"branchTwist0", "branchTwist1", "branchTwist2", "branchTwist3",
	// Tree-level
	"treeType",
	// Leaves
	"leafCount", "leafSize", "leafSizeVariance", "leafAngle", "leafStart",
	"leafBillboard", "leafAlphaTest", "leafType", "leafRoundedNormals",
	// Bark
	"barkType", "barkTint", "barkFlatShading", "barkTextured", "barkTextureScaleX", "barkTextureScaleY",
	// Force
	"forceDirectionX", "forceDirectionY", "forceDirectionZ", "forceStrength",
];

/** Which flat keys are non-swingable KINDS (enum/bool/vec3) — these never vary between variants
 *  even if a swing is somehow set (§4.13). The `deriveVariants` P2 guard already refuses to swing
 *  `enum`/`bool`/`vec3` carriers by KIND; this is a secondary explicit list for the keys whose
 *  VALUE is a bare number but which semantically must not swing (e.g. `seed`). */
export const EZTREE_NON_SWINGABLE_KEYS: ReadonlySet<string> = new Set([
	"treeType", "leafBillboard", "leafType", "barkType", "barkFlatShading", "barkTextured",
	"forceDirectionX", "forceDirectionY", "forceDirectionZ",
]);

// ── Flat ⇄ nested mapping ────────────────────────────────────────────────────────────────────
// Gen_EzTree consumes a resolved variant's flat params and produces a `TreeOptions`. The mapping
// is a straight port of ez-tree's `TreeOptions` structure.

/** Build a `TreeOptions` from a flat params object. `params` is a `Record<string, ParamValue>`
 *  (a resolved variant's params) — only the EZTREE_FLAT_KEYS are read; everything else is ignored.
 *  `base` is the TreeOptions to start from (defaults to a fresh `new TreeOptions()`); present flat
 *  keys override it, absent keys keep the base's value — so a partial flat object is a merge, not
 *  a replace (the flat-scenario hazard, §4.2 c: absent keys never become `undefined`).
 *
 *  Returns a NEW TreeOptions each call (the caller must not share/mutate one across variants —
 *  ez-tree's `options` drives the RNG + meshing). */
export function flatToTreeOptions(params: Record<string, ParamValue>, base?: TreeOptions): TreeOptions {
	const o = base ?? new TreeOptions();
	const num = (key: string): number|n => {
		const v = params[key];
		return (typeof v == "number" && Number.isFinite(v)) ? v : undefined;
	};
	const numOr = (key: string, fallback: number): number => {
		const v = num(key);
		return v == null ? fallback : v;
	};
	// Branch levels
	const levels = numOr("branchLevels", 3);
	o.branch.levels = levels;

	// Per-level maps — only build entries for the levels that exist (level `levels`+… are ignored).
	const putLevelMap = (opt: string, keyPrefix: string, max: number): void => {
		const branch = o.branch as unknown as Record<string, LevelMap>;
		const map: LevelMap = {};
		for (let lv = 0; lv <= max; lv++) {
			const v = num(`${keyPrefix}${lv}`);
			if (v != null) map[lv] = v;
		}
		branch[opt] = map;
	};
	putLevelMap("angle", "branchAngle", 3);
	putLevelMap("children", "branchChildren", 2);
	putLevelMap("gnarliness", "branchGnarliness", 3);
	putLevelMap("length", "branchLength", 3);
	putLevelMap("radius", "branchRadius", 3);
	putLevelMap("sections", "branchSections", 3);
	putLevelMap("segments", "branchSegments", 3);
	putLevelMap("taper", "branchTaper", 3);
	putLevelMap("twist", "branchTwist", 3);
	// `start` levels 1..3 (0 has no start — a trunk has no parent).
	{
		const map: LevelMap = {};
		for (let lv = 1; lv <= 3; lv++) {
			const v = num(`branchStart${lv}`);
			if (v != null) map[lv] = v;
		}
		o.branch.start = map;
	}

	// Tree type
	const treeType = params.treeType;
	if (typeof treeType == "string") o.type = treeType == "evergreen" ? "evergreen" : "deciduous";

	// Leaves
	const leafCount = num("leafCount");
	if (leafCount != null) o.leaves.count = leafCount;
	const leafSize = num("leafSize");
	if (leafSize != null) o.leaves.size = leafSize;
	const leafSizeVariance = num("leafSizeVariance");
	if (leafSizeVariance != null) o.leaves.sizeVariance = leafSizeVariance;
	const leafAngle = num("leafAngle");
	if (leafAngle != null) o.leaves.angle = leafAngle;
	const leafStart = num("leafStart");
	if (leafStart != null) o.leaves.start = leafStart;
	const leafBillboard = params.leafBillboard;
	if (typeof leafBillboard == "string") o.leaves.billboard = (leafBillboard as string) == "single" ? "single" : "double";
	const leafAlphaTest = num("leafAlphaTest");
	if (leafAlphaTest != null) o.leaves.alphaTest = leafAlphaTest;
	// `leafType` (the §4.9 leaf-sprite selector) maps to `o.leaves.type` — the informational id
	// the baker uses to resolve the leaf sprite. It does NOT change geometry (the material is
	// renderer-side), but it belongs in the flat schema so the UI exposes it and the preset
	// codegen round-trips it.
	const leafType = params.leafType;
	if (typeof leafType == "string") o.leaves.type = leafType;
	const leafRoundedNormals = params.leafRoundedNormals;
	if (typeof leafRoundedNormals == "boolean") o.leaves.roundedNormals = leafRoundedNormals;

	// Bark
	const barkType = params.barkType;
	if (typeof barkType == "string") o.bark.type = barkType;
	const barkTint = num("barkTint");
	if (barkTint != null) o.bark.tint = barkTint;
	const barkFlatShading = params.barkFlatShading;
	if (typeof barkFlatShading == "boolean") o.bark.flatShading = barkFlatShading;
	const barkTextured = params.barkTextured;
	if (typeof barkTextured == "boolean") o.bark.textured = barkTextured;
	const barkTextureScaleX = num("barkTextureScaleX");
	if (barkTextureScaleX != null) o.bark.textureScale.x = barkTextureScaleX;
	const barkTextureScaleY = num("barkTextureScaleY");
	if (barkTextureScaleY != null) o.bark.textureScale.y = barkTextureScaleY;

	// Force
	const fdx = num("forceDirectionX"), fdy = num("forceDirectionY"), fdz = num("forceDirectionZ");
	if (fdx != null || fdy != null || fdz != null) {
		o.branch.force.direction.x = fdx ?? o.branch.force.direction.x;
		o.branch.force.direction.y = fdy ?? o.branch.force.direction.y;
		o.branch.force.direction.z = fdz ?? o.branch.force.direction.z;
	}
	const forceStrength = num("forceStrength");
	if (forceStrength != null) o.branch.force.strength = forceStrength;

	return o;
}

/** Inverse mapping for round-trip tests: the nested `TreeOptions` → flat keys that exist in the
 *  EZTREE_FLAT_KEYS list. NOTE: this is NOT lossless for every field (per-level maps only carry
 *  levels 0..max, and leaf/bark `type`/`tint` etc. are copied), but it IS the inverse of the
 *  subset the generator reads — so a round-trip `options → flat → options` (via `flatToTreeOptions`
 *  with the same `base`) reproduces the SAME values. Used by the P6 round-trip test (§7). */
export function treeOptionsToFlat(o: TreeOptions): Record<string, ParamValue> {
	const out: Record<string, ParamValue> = {};
	out.branchLevels = o.branch.levels ?? 3;
	out.treeType = o.type;
	const putMap = (opt: string, keyPrefix: string, max: number): void => {
		const map = (o.branch as unknown as Record<string, LevelMap>)[opt] || {};
		for (let lv = 0; lv <= max; lv++) if (map[lv] != null) out[`${keyPrefix}${lv}`] = map[lv] as number;
	};
	putMap("angle", "branchAngle", 3);
	putMap("children", "branchChildren", 2);
	putMap("gnarliness", "branchGnarliness", 3);
	putMap("length", "branchLength", 3);
	putMap("radius", "branchRadius", 3);
	putMap("sections", "branchSections", 3);
	putMap("segments", "branchSegments", 3);
	putMap("taper", "branchTaper", 3);
	putMap("twist", "branchTwist", 3);
	for (let lv = 1; lv <= 3; lv++) if (o.branch.start[lv] != null) out[`branchStart${lv}`] = o.branch.start[lv] as number;
	out.leafCount = o.leaves.count;
	out.leafSize = o.leaves.size;
	out.leafSizeVariance = o.leaves.sizeVariance;
	out.leafAngle = o.leaves.angle;
	out.leafStart = o.leaves.start;
	out.leafBillboard = o.leaves.billboard;
	out.leafAlphaTest = o.leaves.alphaTest;
	out.leafType = o.leaves.type;
	out.leafRoundedNormals = o.leaves.roundedNormals;
	out.barkType = o.bark.type;
	out.barkTint = o.bark.tint;
	out.barkFlatShading = o.bark.flatShading;
	out.barkTextured = o.bark.textured;
	out.barkTextureScaleX = o.bark.textureScale.x;
	out.barkTextureScaleY = o.bark.textureScale.y;
	out.forceDirectionX = o.branch.force.direction.x;
	out.forceDirectionY = o.branch.force.direction.y;
	out.forceDirectionZ = o.branch.force.direction.z;
	out.forceStrength = o.branch.force.strength;
	return out;
}
